/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import { fonts } from "../../themes/dashboard";

const CHIP_COLOR = "#2B4FB9";
const HEADER_COLOR = "#1E3A8A";

export const DepartmentsTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 16,
    minWidth: 240,
    maxWidth: 300,
    fontFamily: fonts?.fontFamily,
  },
}));

export const ChipRoot = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  // Fits the table cell, which clips its overflow, so a long name ellipsizes and the +N count stays visible.
  boxSizing: "border-box",
  maxWidth: "min(200px, 100%)",
  height: 28,
  paddingLeft: 10,
  paddingRight: 6,
  borderRadius: 8,
  backgroundColor: "#EAF0FC",
  color: CHIP_COLOR,
  cursor: "default",
  whiteSpace: "nowrap",
}));

export const ChipDot = styled("span")(() => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: CHIP_COLOR,
  flexShrink: 0,
}));

export const ChipLabel = styled(Typography)(() => ({
  color: CHIP_COLOR,
  fontSize: 14,
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

export const CountBadge = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 22,
  height: 20,
  paddingLeft: 5,
  paddingRight: 5,
  borderRadius: 6,
  backgroundColor: "#D3DFF8",
  color: CHIP_COLOR,
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
}));

export const TooltipHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  paddingBottom: 10,
  marginBottom: 8,
  borderBottom: "1px solid #E5E7EB",
}));

export const TooltipTitle = styled(Typography)(() => ({
  color: HEADER_COLOR,
  fontSize: 15,
  fontWeight: 700,
}));

export const TotalBadge = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 22,
  height: 22,
  paddingLeft: 6,
  paddingRight: 6,
  borderRadius: 999,
  backgroundColor: "#EAF0FC",
  color: HEADER_COLOR,
  fontSize: 12,
  fontWeight: 600,
}));

export const DepartmentList = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  maxHeight: 280,
  overflowY: "auto",
}));

export const DepartmentRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  paddingTop: 7,
  paddingBottom: 7,
}));

export const DepartmentName = styled(Typography)(() => ({
  color: "#1F2937",
  fontSize: 14,
  wordBreak: "break-word",
}));

export const EmptyLabel = styled(Typography)(() => ({
  color: "#71717A",
  fontSize: 14,
}));
